'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useCurrency } from '../../hooks';
import { DEFAULT_CURRENCY, ORDER_LIMITS } from '@/constants';
import styles from './Cart.module.css';
import type { PaymentMethod } from './CartSummary';

interface BalancePanelProps {
  userBalanceCents: number;
  isEmailVerified: boolean;
  balanceApplyCents: number;
  setBalanceApplyCents: (cents: number) => void;
  selectedCurrency: string;
  paymentMethod: PaymentMethod;
  // Cart total in the order currency (post-coupon, pre-balance). Undefined
  // while pricing is loading - we cap at full balance in that case and
  // let the server do the authoritative clamp.
  totalInOrderCurrency: number | undefined;
  // Fires whenever the applied-balance amount is enough to cover the full
  // cart (within a 0.01-ccy epsilon). The parent reads this to switch the
  // checkout button label from "Proceed to checkout" to "Confirm order".
  onFullyCoveredChange?: (isCovered: boolean) => void;
}

// Pay-with-balance UI block. Owns: EUR→ccy rate fetch, slider/input state,
// quick-pick buttons, the preview line. Does NOT own the applied-balance
// cents - that lives on the parent because useCartLogic needs to send it
// on checkout. Input text is decoupled from cents so the user can type
// freely without getting reformatted on every keystroke.
const BalancePanel: React.FC<BalancePanelProps> = ({
  userBalanceCents,
  isEmailVerified,
  balanceApplyCents,
  setBalanceApplyCents,
  selectedCurrency,
  paymentMethod,
  totalInOrderCurrency,
  onFullyCoveredChange,
}) => {
  const t = useTranslations('cart');
  const { getRate } = useCurrency();
  const [eurToOrderRate, setEurToOrderRate] = useState<number | null>(null);
  // USD→order-currency rate, used to express ORDER_LIMITS.minOrderUsd (a USD
  // figure) in the order currency. null while loading or when the order is
  // already in USD (rate is then implicitly 1).
  const [usdToOrderRate, setUsdToOrderRate] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (userBalanceCents > 0 && isEmailVerified && selectedCurrency) {
      getRate('EUR', selectedCurrency)
        .then((r) => {
          if (!cancelled) setEurToOrderRate(r?.rate ?? null);
        })
        .catch(() => {
          if (!cancelled) setEurToOrderRate(null);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [userBalanceCents, isEmailVerified, selectedCurrency, getRate]);

  useEffect(() => {
    let cancelled = false;
    if (selectedCurrency === DEFAULT_CURRENCY) {
      setUsdToOrderRate(1);
      return;
    }
    if (userBalanceCents > 0 && isEmailVerified && selectedCurrency) {
      getRate(DEFAULT_CURRENCY, selectedCurrency)
        .then((r) => {
          if (!cancelled) setUsdToOrderRate(r?.rate ?? null);
        })
        .catch(() => {
          if (!cancelled) setUsdToOrderRate(null);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [userBalanceCents, isEmailVerified, selectedCurrency, getRate]);

  // Balance (EUR cents) needed to cover the order in full. 0 until we have
  // both a cart total and a rate.
  const fullCoverageCents = useMemo(() => {
    if (!totalInOrderCurrency || totalInOrderCurrency <= 0) return 0;
    if (!eurToOrderRate || eurToOrderRate <= 0) return 0;
    return Math.round((totalInOrderCurrency / eurToOrderRate) * 100);
  }, [totalInOrderCurrency, eurToOrderRate]);

  // The minimum order, expressed in the order currency. undefined while the
  // USD→order rate is loading - callers then fall back to server enforcement.
  const minOrderInOrderCcy = useMemo(() => {
    if (!usdToOrderRate || usdToOrderRate <= 0) return undefined;
    return ORDER_LIMITS.minOrderUsd * usdToOrderRate;
  }, [usdToOrderRate]);

  // Whether the balance is enough to cover the whole order.
  const canCoverFull =
    fullCoverageCents > 0 && userBalanceCents >= fullCoverageCents;

  // Largest PARTIAL balance (EUR cents) that still leaves a chargeable
  // residual (>= the minimum). The payment provider can't open a session
  // below the minimum, so values between this and full coverage are invalid -
  // the user must either stay at/under this or cover the order in full.
  const partialMaxCents = useMemo(() => {
    if (!userBalanceCents || userBalanceCents <= 0) return 0;
    if (
      !totalInOrderCurrency ||
      totalInOrderCurrency <= 0 ||
      !eurToOrderRate ||
      eurToOrderRate <= 0 ||
      minOrderInOrderCcy === undefined
    ) {
      // Not enough info to locate the dead zone yet; let the server clamp.
      return Math.min(userBalanceCents, fullCoverageCents || userBalanceCents);
    }
    const partialCcy = totalInOrderCurrency - minOrderInOrderCcy;
    if (partialCcy <= 0) return 0; // order == minimum: no partial is possible
    return Math.min(
      userBalanceCents,
      Math.floor((partialCcy / eurToOrderRate) * 100),
    );
  }, [
    userBalanceCents,
    totalInOrderCurrency,
    eurToOrderRate,
    minOrderInOrderCcy,
    fullCoverageCents,
  ]);

  // Ceiling for the continuous slider/input. When the balance can cover the
  // order in full, the slider may travel all the way to full coverage (the
  // dead zone just below it is snapped away by clampApply). Otherwise it stops
  // at the largest valid partial amount.
  const maxApplyCents = useMemo(() => {
    if (!userBalanceCents || userBalanceCents <= 0) return 0;
    return canCoverFull ? fullCoverageCents : partialMaxCents;
  }, [userBalanceCents, canCoverFull, fullCoverageCents, partialMaxCents]);

  // Snap an arbitrary requested apply (EUR cents) to the nearest VALID amount:
  // 0, a partial that leaves >= the minimum, or exact full coverage. Never
  // returns a value that would leave a residual the provider can't charge.
  const clampApply = useCallback(
    (cents: number): number => {
      if (!Number.isFinite(cents) || cents <= 0) return 0;
      const ceiling = canCoverFull
        ? fullCoverageCents
        : Math.min(userBalanceCents, fullCoverageCents || userBalanceCents);
      const v = Math.min(Math.round(cents), ceiling);

      // Missing rate/total info → defer to the server's authoritative clamp.
      if (
        minOrderInOrderCcy === undefined ||
        !eurToOrderRate ||
        eurToOrderRate <= 0 ||
        !totalInOrderCurrency ||
        totalInOrderCurrency <= 0
      ) {
        return v;
      }

      const residual = totalInOrderCurrency - (v / 100) * eurToOrderRate;
      if (residual <= 0.009) return fullCoverageCents; // fully covered
      if (residual >= minOrderInOrderCcy - 1e-9) return v; // valid partial
      // Dead zone (0 < residual < minimum): cover in full if we can, else
      // fall back to the largest valid partial.
      return canCoverFull ? fullCoverageCents : partialMaxCents;
    },
    [
      canCoverFull,
      fullCoverageCents,
      userBalanceCents,
      minOrderInOrderCcy,
      eurToOrderRate,
      totalInOrderCurrency,
      partialMaxCents,
    ],
  );

  const commitApply = useCallback(
    (cents: number) => setBalanceApplyCents(clampApply(cents)),
    [clampApply, setBalanceApplyCents],
  );

  const balanceInputFocusedRef = useRef(false);

  // Re-snap the controlled value whenever constraints change (cart shrinks,
  // rates load). Keeps a stale value out of the dead zone / above the max.
  // Skipped while the input is focused so active typing isn't reformatted
  // mid-keystroke - blur runs the same snap.
  useEffect(() => {
    if (balanceInputFocusedRef.current) return;
    const snapped = clampApply(balanceApplyCents);
    if (snapped !== balanceApplyCents) {
      setBalanceApplyCents(snapped);
    }
  }, [balanceApplyCents, clampApply, setBalanceApplyCents]);

  // Local text state for the EUR input. Decoupled from `balanceApplyCents`
  // so the user can type "5" on their way to "50" without the controlled
  // value snapping them back to "0.05". Commits to cents on parseable
  // input and re-formats on blur.
  const [balanceInputText, setBalanceInputText] = useState<string>(() =>
    (Math.min(balanceApplyCents, maxApplyCents) / 100).toFixed(2),
  );
  useEffect(() => {
    if (balanceInputFocusedRef.current) return;
    setBalanceInputText(
      (Math.min(balanceApplyCents, maxApplyCents) / 100).toFixed(2),
    );
  }, [balanceApplyCents, maxApplyCents]);

  const handleBalanceTextChange = (raw: string) => {
    const sanitized = raw
      .replace(',', '.')
      .replace(/[^\d.]/g, '')
      .replace(/(\..*)\./g, '$1')
      .replace(/^(\d*\.\d{0,2}).*$/, '$1');
    setBalanceInputText(sanitized);

    if (sanitized === '' || sanitized === '.') {
      setBalanceApplyCents(0);
      return;
    }
    const n = Number(sanitized);
    if (!Number.isFinite(n) || n < 0) return;
    const cents = Math.min(maxApplyCents, Math.round(n * 100));
    setBalanceApplyCents(cents);
  };

  const handleBalanceBlur = () => {
    balanceInputFocusedRef.current = false;
    const snapped = clampApply(balanceApplyCents);
    setBalanceApplyCents(snapped);
    setBalanceInputText((snapped / 100).toFixed(2));
  };

  const applyBalancePortion = (fraction: number) => {
    commitApply(Math.round(maxApplyCents * fraction));
  };

  const balanceAppliedInOrderCcy =
    eurToOrderRate && balanceApplyCents > 0
      ? (balanceApplyCents / 100) * eurToOrderRate
      : 0;
  const remainingToPay =
    totalInOrderCurrency !== undefined
      ? Math.max(0, totalInOrderCurrency - balanceAppliedInOrderCcy)
      : undefined;
  const isFullyCoveredByBalance =
    remainingToPay !== undefined && remainingToPay <= 0.009;

  // Show the "minimum must stay payable" hint when the user has MORE balance
  // than they can apply: the order can't be covered in full, and the rest is
  // held back so a chargeable residual (>= the minimum) remains.
  const showMinResidualNote =
    !canCoverFull &&
    minOrderInOrderCcy !== undefined &&
    partialMaxCents > 0 &&
    partialMaxCents < userBalanceCents;

  // Report the "fully covered" boolean up so the parent can re-label the
  // checkout button. useEffect (not inline) to avoid setState-during-render.
  useEffect(() => {
    onFullyCoveredChange?.(isFullyCoveredByBalance);
  }, [isFullyCoveredByBalance, onFullyCoveredChange]);

  if (!isEmailVerified || userBalanceCents <= 0) return null;

  return (
    <div className={styles.balancePanel}>
      <div className={styles.balancePanelHeader}>
        <strong>{t('summary.accountBalance.title')}</strong>
        <span className={styles.balancePanelAvailable}>
          {t('summary.accountBalance.available', {
            amount: (userBalanceCents / 100).toFixed(2),
          })}
        </span>
      </div>

      <div className={styles.balancePanelSliderRow}>
        <input
          type="range"
          min={0}
          max={maxApplyCents}
          step={1}
          value={Math.min(balanceApplyCents, maxApplyCents)}
          onChange={(e) => commitApply(parseInt(e.target.value, 10))}
          className={styles.balancePanelSlider}
          aria-label={t('summary.accountBalance.inputLabel')}
        />
        <div className={styles.balancePanelInputWrap}>
          <input
            type="text"
            inputMode="decimal"
            value={balanceInputText}
            onChange={(e) => handleBalanceTextChange(e.target.value)}
            onFocus={() => {
              balanceInputFocusedRef.current = true;
            }}
            onBlur={handleBalanceBlur}
            className={styles.balancePanelInput}
            aria-label={t('summary.accountBalance.inputLabel')}
          />
          <span className={styles.balancePanelInputSuffix}>EUR</span>
        </div>
      </div>

      <div className={styles.balancePanelQuick}>
        <button
          type="button"
          onClick={() => applyBalancePortion(0.25)}
          disabled={maxApplyCents === 0}
          className={styles.balancePanelQuickBtn}
        >
          {t('summary.accountBalance.quick25')}
        </button>
        <button
          type="button"
          onClick={() => applyBalancePortion(0.5)}
          disabled={maxApplyCents === 0}
          className={styles.balancePanelQuickBtn}
        >
          {t('summary.accountBalance.quick50')}
        </button>
        <button
          type="button"
          onClick={() => applyBalancePortion(1)}
          disabled={maxApplyCents === 0}
          className={styles.balancePanelQuickBtn}
        >
          {t('summary.accountBalance.quickMax')}
        </button>
        <button
          type="button"
          onClick={() => setBalanceApplyCents(0)}
          disabled={balanceApplyCents === 0}
          className={`${styles.balancePanelQuickBtn} ${styles.balancePanelQuickBtnGhost}`}
        >
          {t('summary.accountBalance.clear')}
        </button>
      </div>

      {showMinResidualNote && (
        <div className={styles.balancePanelMuted}>
          {t('summary.accountBalance.minResidualNote', {
            min: ORDER_LIMITS.minOrderUsd,
            amount: (partialMaxCents / 100).toFixed(2),
          })}
        </div>
      )}

      <div className={styles.balancePanelPreview}>
        {balanceApplyCents > 0 ? (
          <>
            <div>
              {t('summary.accountBalance.applied', {
                amount: (balanceApplyCents / 100).toFixed(2),
              })}
              {selectedCurrency !== 'EUR' && eurToOrderRate && (
                <span className={styles.balancePanelMuted}>
                  {' '}
                  (
                  {t('summary.accountBalance.appliedWithConversion', {
                    ccy: selectedCurrency,
                    amountCcy: balanceAppliedInOrderCcy.toFixed(2),
                  })}
                  )
                </span>
              )}
            </div>
            {!isFullyCoveredByBalance && remainingToPay !== undefined && (
              <div>
                {paymentMethod === 'crypto'
                  ? t('summary.accountBalance.remainingCrypto')
                  : t('summary.accountBalance.remainingBank')}
                :{' '}
                <strong>
                  {selectedCurrency} {remainingToPay.toFixed(2)}
                </strong>
              </div>
            )}
          </>
        ) : (
          <span className={styles.balancePanelMuted}>
            {t('summary.accountBalance.helpDrag')}
          </span>
        )}
      </div>
    </div>
  );
};

export default BalancePanel;
